/**
 * iter845i — Online Ordering admin console.
 * Route: /settings/online-ordering  (admin + manager).
 * Per-branch configuration: enable, hours, prep time, payment methods,
 * confirmation channels, tables + QR downloads.
 */
import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Store, Save, QrCode, Trash2, Copy, ExternalLink, Plus, Download, ChevronDown, ChevronRight, KeyRound, Shield, CreditCard, MessageCircle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABEL = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
const PM_OPTIONS = [
  { key: 'cash_on_pickup', label: 'Cash on Pickup' },
  { key: 'pay_at_counter', label: 'Pay at Counter' },
  { key: 'online_card', label: 'Card / Apple Pay (online)' },
];
const CH_OPTIONS = [
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'sms', label: 'SMS' },
  { key: 'email', label: 'Email' },
];

function BranchOnlineCard({ branch, onUpdated }) {
  const [expanded, setExpanded] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [tables, setTables] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newTableName, setNewTableName] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/branches/${branch.id}/online-config`);
      setCfg(data);
      setTables(data.tables || []);
    } catch { toast.error('Failed to load branch config'); }
  }, [branch.id]);

  useEffect(() => { if (expanded && !cfg) load(); }, [expanded, cfg, load]);

  const publicUrl = cfg?.online_slug ? `${window.location.origin}/order/${cfg.online_slug}` : null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/branches/${branch.id}/online-config`, {
        online_ordering_enabled: cfg.online_ordering_enabled,
        online_pickup_enabled: cfg.online_pickup_enabled,
        online_dine_in_enabled: cfg.online_dine_in_enabled,
        online_prep_time_min: cfg.online_prep_time_min,
        online_hours: cfg.online_hours,
        online_payment_methods: cfg.online_payment_methods,
        online_confirmation_channels: cfg.online_confirmation_channels,
        online_require_email: cfg.online_require_email,
      });
      setCfg(data);
      toast.success('Saved');
      onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const addTable = async () => {
    if (!newTableName.trim()) return;
    try {
      const { data } = await api.post(`/branches/${branch.id}/tables`, { name: newTableName.trim() });
      setTables((prev) => [...prev, data]);
      setNewTableName('');
      toast.success(`${data.name} added`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Add failed'); }
  };
  const removeTable = async (id) => {
    if (!window.confirm('Delete this table? Its QR code will stop working.')) return;
    try {
      await api.delete(`/branches/${branch.id}/tables/${id}`);
      setTables((prev) => prev.filter((t) => t.id !== id));
    } catch { toast.error('Delete failed'); }
  };

  const downloadQR = (table) => {
    const base = window.location.origin;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/branches/${branch.id}/tables/${table.id}/qr.png?base_url=${encodeURIComponent(base)}`;
    // Use fetch with auth header; then trigger download from blob.
    const token = localStorage.getItem('token');
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.blob() : Promise.reject(r.status))
      .then((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `qr-${branch.name}-${table.name}.png`;
        link.click();
      })
      .catch(() => toast.error('QR download failed'));
  };

  const toggleArr = (arr, key) => arr.includes(key) ? arr.filter((k) => k !== key) : [...arr, key];

  return (
    <Card data-testid={`branch-card-${branch.id}`}>
      <CardContent className="p-3">
        <button
          type="button"
          className="w-full flex items-center gap-3 text-left"
          onClick={() => setExpanded(!expanded)}
          data-testid={`branch-toggle-${branch.id}`}
        >
          <div className="w-9 h-9 rounded-xl bg-stone-100 dark:bg-stone-800 flex items-center justify-center">
            <Store size={16} className="text-stone-600 dark:text-stone-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm truncate">{branch.name}</span>
              {branch.online_ordering_enabled
                ? <Badge className="bg-emerald-600 text-white text-[9px]">Online</Badge>
                : <Badge variant="outline" className="text-[9px]">Offline</Badge>}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {branch.online_slug ? `/order/${branch.online_slug}` : 'no public link yet'} · {branch.table_count} tables
            </div>
          </div>
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        {expanded && cfg && (
          <div className="mt-4 space-y-4 pl-1 pr-1" data-testid={`branch-expanded-${branch.id}`}>
            {/* Master enable */}
            <div className="flex items-center justify-between bg-stone-50 dark:bg-stone-800/40 rounded-lg px-3 py-2">
              <div>
                <div className="text-xs font-bold">Enable online ordering</div>
                <div className="text-[10px] text-muted-foreground">Customers can place orders via public link + QR</div>
              </div>
              <Switch
                checked={!!cfg.online_ordering_enabled}
                onCheckedChange={(v) => setCfg({ ...cfg, online_ordering_enabled: v })}
                data-testid={`toggle-enable-${branch.id}`}
              />
            </div>

            {/* Public link */}
            {publicUrl && (
              <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg px-3 py-2">
                <span className="text-xs font-semibold text-emerald-800 dark:text-emerald-300 truncate flex-1" data-testid={`public-url-${branch.id}`}>{publicUrl}</span>
                <Button size="sm" variant="ghost" className="h-7" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Link copied'); }}><Copy size={12} /></Button>
                <a href={publicUrl} target="_blank" rel="noreferrer" className="h-7 w-7 rounded-md hover:bg-emerald-100 dark:hover:bg-emerald-900/50 flex items-center justify-center"><ExternalLink size={12} /></a>
              </div>
            )}

            {/* Order types */}
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2 bg-white dark:bg-stone-800/40 rounded-lg px-3 py-2 border border-border cursor-pointer">
                <Switch checked={!!cfg.online_dine_in_enabled} onCheckedChange={(v) => setCfg({ ...cfg, online_dine_in_enabled: v })} data-testid={`toggle-dinein-${branch.id}`} />
                <span className="text-xs font-semibold">Dine-in</span>
              </label>
              <label className="flex items-center gap-2 bg-white dark:bg-stone-800/40 rounded-lg px-3 py-2 border border-border cursor-pointer">
                <Switch checked={!!cfg.online_pickup_enabled} onCheckedChange={(v) => setCfg({ ...cfg, online_pickup_enabled: v })} data-testid={`toggle-pickup-${branch.id}`} />
                <span className="text-xs font-semibold">Pickup</span>
              </label>
            </div>

            {/* Prep time */}
            <div>
              <Label className="text-[10px]">Estimated prep time (min)</Label>
              <Input
                type="number" min={1} max={240}
                value={cfg.online_prep_time_min || 0}
                onChange={(e) => setCfg({ ...cfg, online_prep_time_min: parseInt(e.target.value, 10) || 0 })}
                className="h-8 mt-0.5 w-24"
                data-testid={`input-prep-${branch.id}`}
              />
            </div>

            {/* Payment methods */}
            <div>
              <Label className="text-[10px]">Payment methods (public checkout)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {PM_OPTIONS.map((o) => {
                  const on = (cfg.online_payment_methods || []).includes(o.key);
                  return (
                    <button
                      key={o.key} type="button"
                      onClick={() => setCfg({ ...cfg, online_payment_methods: toggleArr(cfg.online_payment_methods || [], o.key) })}
                      className={`h-7 px-2.5 rounded-full text-[10px] font-semibold border transition-colors ${on ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-300'}`}
                      data-testid={`pm-${branch.id}-${o.key}`}
                    >{o.label}</button>
                  );
                })}
              </div>
            </div>

            {/* Confirmation channels */}
            <div>
              <Label className="text-[10px]">Order confirmation channels</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {CH_OPTIONS.map((o) => {
                  const on = (cfg.online_confirmation_channels || []).includes(o.key);
                  return (
                    <button
                      key={o.key} type="button"
                      onClick={() => setCfg({ ...cfg, online_confirmation_channels: toggleArr(cfg.online_confirmation_channels || [], o.key) })}
                      className={`h-7 px-2.5 rounded-full text-[10px] font-semibold border transition-colors ${on ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-stone-600 border-stone-300'}`}
                      data-testid={`ch-${branch.id}-${o.key}`}
                    >{o.label}</button>
                  );
                })}
              </div>
              <label className="flex items-center gap-2 mt-2 text-[10px]">
                <Switch checked={!!cfg.online_require_email} onCheckedChange={(v) => setCfg({ ...cfg, online_require_email: v })} data-testid={`toggle-email-req-${branch.id}`} />
                Require email at checkout
              </label>
              <label className="flex items-center gap-2 mt-1 text-[10px]">
                <Switch
                  checked={cfg.online_status_updates_enabled !== false}
                  onCheckedChange={(v) => setCfg({ ...cfg, online_status_updates_enabled: v })}
                  data-testid={`toggle-status-updates-${branch.id}`}
                />
                <span>
                  <b>Auto WhatsApp status updates</b> — customer gets a DM when order flips to <i>Preparing 👨‍🍳</i>, <i>Ready 🥡</i>, or <i>Completed ✅</i>. (Needs Twilio configured.)
                </span>
              </label>
            </div>

            {/* Ordering hours */}
            <div>
              <Label className="text-[10px]">Ordering hours (leave blank = closed that day; empty = always open)</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
                {DAYS.map((d) => {
                  const val = cfg.online_hours?.[d] || {};
                  const setDay = (patch) => setCfg({ ...cfg, online_hours: { ...cfg.online_hours, [d]: { ...val, ...patch } } });
                  return (
                    <div key={d} className="border border-border rounded-lg p-2">
                      <div className="text-[10px] font-bold text-stone-500 uppercase tracking-wider mb-1">{DAY_LABEL[d]}</div>
                      <Input type="time" value={val.open || ''} onChange={(e) => setDay({ open: e.target.value })} className="h-7 text-[10px]" data-testid={`hr-${branch.id}-${d}-open`} />
                      <Input type="time" value={val.close || ''} onChange={(e) => setDay({ close: e.target.value })} className="h-7 text-[10px] mt-1" data-testid={`hr-${branch.id}-${d}-close`} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Tables */}
            <div>
              <Label className="text-[10px]">Tables (each gets a QR code)</Label>
              <div className="flex gap-2 mt-1">
                <Input value={newTableName} onChange={(e) => setNewTableName(e.target.value)} placeholder="e.g. Table 5 / Bar-Seat-2" className="h-8 flex-1" data-testid={`new-table-input-${branch.id}`} onKeyDown={(e) => e.key === 'Enter' && addTable()} />
                <Button size="sm" onClick={addTable} className="h-8" data-testid={`add-table-btn-${branch.id}`}><Plus size={12} className="mr-1" /> Add</Button>
              </div>
              <div className="mt-2 space-y-1">
                {tables.length === 0 ? (
                  <div className="text-[11px] text-muted-foreground italic py-2 text-center">No tables yet.</div>
                ) : tables.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 bg-stone-50 dark:bg-stone-800/40 rounded-lg px-2.5 py-1.5" data-testid={`table-row-${t.id}`}>
                    <QrCode size={13} className="text-stone-500" />
                    <span className="text-xs font-semibold flex-1 truncate">{t.name}</span>
                    <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => downloadQR(t)} disabled={!cfg.online_slug} title={cfg.online_slug ? 'Download QR PNG' : 'Enable online ordering first'} data-testid={`qr-download-${t.id}`}><Download size={11} className="mr-1" /> QR</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-rose-500" onClick={() => removeTable(t.id)} data-testid={`table-delete-${t.id}`}><Trash2 size={11} /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={save} disabled={saving} className="h-9" data-testid={`save-btn-${branch.id}`}>
                <Save size={13} className="mr-1" /> {saving ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MyFatoorahHelpCard() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api.get('/admin/payments/myfatoorah/status')
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ configured: false, is_sandbox: true, base_url: '', webhook_url: '', webhook_secret_set: false }));
  }, []);

  const link = ({ href, children, testid }) => (
    <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 hover:underline font-semibold" data-testid={testid}>
      {children} <ExternalLink size={10} />
    </a>
  );

  return (
    <Card className="border-emerald-200 bg-emerald-50/40 dark:bg-emerald-950/10 dark:border-emerald-900" data-testid="myfatoorah-help-card">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0"><CreditCard size={17} /></div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-bold">MyFatoorah — Card / Apple Pay / Mada / STC Pay</h3>
              {status?.configured ? (
                <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[9px]">✓ Configured ({status.is_sandbox ? 'Sandbox' : 'Production'})</Badge>
              ) : (
                <Badge variant="outline" className="border-amber-500 text-amber-700 text-[9px]">Not configured</Badge>
              )}
              {status?.webhook_secret_set === false && status?.configured && (
                <Badge variant="outline" className="border-amber-500 text-amber-700 text-[9px]">Webhook unsigned</Badge>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground mt-0.5">Set once — every branch that enables &quot;Card / Apple Pay&quot; checkout uses these credentials.</p>

            <div className="mt-3 grid sm:grid-cols-2 gap-2.5 text-[11px]">
              <div className="bg-white dark:bg-stone-900/40 rounded-lg p-2.5 border">
                <div className="flex items-center gap-1.5 font-bold"><KeyRound size={11} /> Step 1 · Get your API token</div>
                <ol className="mt-1 ml-4 list-decimal text-[10.5px] space-y-0.5 text-stone-600 dark:text-stone-300">
                  <li>Register at {link({ href: 'https://portal.myfatoorah.com/', children: 'portal.myfatoorah.com', testid: 'mf-link-portal' })} (or {link({ href: 'https://demo.myfatoorah.com/', children: 'demo.myfatoorah.com', testid: 'mf-link-demo' })} for sandbox)</li>
                  <li>Go to <b>Settings → API Key</b> — copy the token</li>
                  <li>For sandbox, use the shared token on {link({ href: 'https://docs.myfatoorah.com/docs/test-token', children: 'docs.myfatoorah.com/docs/test-token', testid: 'mf-link-testtoken' })}</li>
                  <li>Paste into <code className="bg-stone-100 dark:bg-stone-800 px-1 rounded">MF_API_TOKEN</code> in <code className="bg-stone-100 dark:bg-stone-800 px-1 rounded">backend/.env</code></li>
                </ol>
              </div>

              <div className="bg-white dark:bg-stone-900/40 rounded-lg p-2.5 border">
                <div className="flex items-center gap-1.5 font-bold"><Shield size={11} /> Step 2 · Set the webhook secret</div>
                <ol className="mt-1 ml-4 list-decimal text-[10.5px] space-y-0.5 text-stone-600 dark:text-stone-300">
                  <li>Portal → <b>Settings → Webhook</b></li>
                  <li>Paste this <b>Webhook URL</b>:
                    <div className="flex items-center gap-1 mt-0.5">
                      <code className="text-[9.5px] bg-stone-100 dark:bg-stone-800 px-1 py-0.5 rounded flex-1 truncate">{status?.webhook_url || `${window.location.origin}/api/public/payments/myfatoorah/webhook`}</code>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(status?.webhook_url || `${window.location.origin}/api/public/payments/myfatoorah/webhook`); toast.success('Copied'); }}
                        className="text-[9px] px-1.5 py-0.5 rounded bg-stone-200 dark:bg-stone-700 hover:bg-stone-300"
                        data-testid="mf-copy-webhook-url"
                      ><Copy size={9} /></button>
                    </div>
                  </li>
                  <li>Enable events: <b>InvoiceStatusChanged</b>, <b>TransactionStatusChanged</b></li>
                  <li>Copy the generated <b>Secret</b> → paste into <code className="bg-stone-100 dark:bg-stone-800 px-1 rounded">MF_WEBHOOK_SECRET</code></li>
                  <li>Docs: {link({ href: 'https://docs.myfatoorah.com/docs/webhook-v2', children: 'Webhook v2 setup', testid: 'mf-link-webhook-docs' })} · {link({ href: 'https://docs.myfatoorah.com/docs/webhook-signature', children: 'Signature format', testid: 'mf-link-signature-docs' })}</li>
                </ol>
              </div>

              <div className="bg-white dark:bg-stone-900/40 rounded-lg p-2.5 border sm:col-span-2">
                <div className="flex items-center gap-1.5 font-bold"><CreditCard size={11} /> Step 3 · Test cards + go live</div>
                <ul className="mt-1 ml-4 list-disc text-[10.5px] space-y-0.5 text-stone-600 dark:text-stone-300">
                  <li>Test cards + Apple Pay simulator: {link({ href: 'https://docs.myfatoorah.com/docs/test-cards', children: 'docs.myfatoorah.com/docs/test-cards', testid: 'mf-link-testcards' })}</li>
                  <li>Apple Pay web setup (domain verification file): {link({ href: 'https://docs.myfatoorah.com/docs/apple-pay', children: 'docs.myfatoorah.com/docs/apple-pay', testid: 'mf-link-applepay' })}</li>
                  <li>Country/currency coverage: {link({ href: 'https://docs.myfatoorah.com/docs/currencies', children: 'docs.myfatoorah.com/docs/currencies', testid: 'mf-link-currencies' })}</li>
                  <li>When going live → switch <code className="bg-stone-100 dark:bg-stone-800 px-1 rounded">MF_BASE_URL</code> to <code className="bg-stone-100 dark:bg-stone-800 px-1 rounded">https://api-sa.myfatoorah.com</code> (KSA), <code className="bg-stone-100 dark:bg-stone-800 px-1 rounded">https://api.myfatoorah.com</code> (KWT default), or the region matching your merchant account.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function OnlineOrderingSettingsPage() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/online-ordering/overview');
      setBranches(data?.branches || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const totalEnabled = branches.filter((b) => b.online_ordering_enabled).length;

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="online-ordering-settings-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center">
            <QrCode size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold">Self-Service Online Ordering</h1>
            <p className="text-xs text-muted-foreground">Per-branch configuration for shareable ordering links + table QR codes. Orders route straight to the branch cashier and auto-print.</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Card><CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Branches</div>
            <div className="text-2xl font-bold mt-1 tabular-nums" data-testid="stat-branches">{branches.length}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Online-enabled</div>
            <div className="text-2xl font-bold mt-1 tabular-nums text-emerald-600" data-testid="stat-enabled">{totalEnabled}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Total tables</div>
            <div className="text-2xl font-bold mt-1 tabular-nums" data-testid="stat-tables">{branches.reduce((s, b) => s + (b.table_count || 0), 0)}</div>
          </CardContent></Card>
        </div>

        <MyFatoorahHelpCard />

        <div className="space-y-2" data-testid="branches-list">
          {loading ? (
            <div className="text-center py-8 text-sm text-muted-foreground">Loading…</div>
          ) : branches.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">No branches yet.</div>
          ) : (
            branches.map((b) => <BranchOnlineCard key={b.id} branch={b} onUpdated={load} />)
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
